import { useState } from "react";
import { Loader2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const POSITIONS = ["PG", "SG", "SF", "PF", "C"];
const EMPTY = { first_name: "", last_name: "", age: "", position: "PG", filipino: null, email: "", mobile: "" };

export default function RegisterDialog({ open, onOpenChange }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.filipino === null) {
            toast.error("Please answer the Filipino heritage question");
            return;
        }
        setSaving(true);
        try {
            await api.post("/registrations", { ...form, age: parseInt(form.age, 10), filipino: form.filipino });
            setDone(true);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    const field = (key, label, props = {}) => (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</Label>
            <Input
                required
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                data-testid={`register-${key.replace("_", "-")}`}
                {...props}
            />
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display font-black uppercase text-2xl">
                        Join the <span className="text-[#FF5500]">League</span>
                    </DialogTitle>
                    <DialogDescription className="text-slate-400 text-sm">
                        Register as a YKPBL player — the league office will reach out with tryout details.
                    </DialogDescription>
                </DialogHeader>
                {done ? (
                    <div data-testid="register-success" className="text-center py-6">
                        <ClipboardCheck className="w-12 h-12 text-[#22C55E] mx-auto mb-4" />
                        <p className="font-display font-bold uppercase text-xl text-white">Registration received</p>
                        <p className="text-sm text-slate-400 mt-2">
                            The league office will contact you at {form.email}.
                        </p>
                        <button
                            data-testid="register-close"
                            onClick={() => {
                                onOpenChange(false);
                                setDone(false);
                                setForm(EMPTY);
                            }}
                            className="mt-6 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-full transition-colors duration-200"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} data-testid="register-form" className="space-y-4 mt-2">
                        <div className="grid grid-cols-2 gap-4">
                            {field("first_name", "First Name")}
                            {field("last_name", "Last Name")}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {field("age", "Age", { type: "number", min: 5, max: 100 })}
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Position</Label>
                                <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                                    <SelectTrigger data-testid="register-position" className="mt-2 bg-[#08080C] border-[#262638] text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                        {POSITIONS.map((p) => (
                                            <SelectItem key={p} value={p}>
                                                {p}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Filipino?</Label>
                            <div className="flex gap-2 mt-2">
                                {[
                                    { v: true, l: "Yes", t: "register-filipino-yes" },
                                    { v: false, l: "No", t: "register-filipino-no" },
                                ].map((o) => (
                                    <button
                                        type="button"
                                        key={o.l}
                                        data-testid={o.t}
                                        onClick={() => setForm({ ...form, filipino: o.v })}
                                        className={`flex-1 font-display font-bold uppercase tracking-widest text-xs py-2.5 rounded-full border transition-colors duration-200 ${
                                            form.filipino === o.v
                                                ? "bg-[#FF5500] border-[#FF5500] text-white"
                                                : "border-[#262638] text-slate-400 hover:border-[#FF5500]"
                                        }`}
                                    >
                                        {o.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {field("email", "Email Address", { type: "email", placeholder: "you@email.com" })}
                        {field("mobile", "Mobile #", { type: "tel", placeholder: "09xx xxx xxxx" })}
                        <button
                            type="submit"
                            data-testid="register-submit"
                            disabled={saving}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest py-3 rounded-full transition-colors duration-200"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Register
                        </button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
